import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/atoms/Button';
import { useAuth } from '@/context/AuthContext';
import { ApiError } from '@/services/api';
import styles from './Login.module.scss';

interface LocationState {
  from?: { pathname: string };
}

export function Login() {
  const { state, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromPath = (location.state as LocationState | null)?.from?.pathname ?? '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (state.status === 'authenticated') {
    return <Navigate to={fromPath} replace />;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate(fromPath, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('Email o contraseña incorrectos.');
      } else if (err instanceof ApiError && err.status === 429) {
        setError('Demasiados intentos. Espera unos minutos antes de volver a intentar.');
      } else {
        setError('Ha habido un problema. Inténtalo de nuevo.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className={styles.page}>
      <div className={styles.card}>
        <h1>Knooowy</h1>
        <p className={styles.subtitle}>Accede con tu cuenta</p>

        <form onSubmit={handleSubmit} className={styles.form} noValidate>
          <label className={styles.field}>
            <span className={styles.label}>Email</span>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={styles.input}
            />
          </label>

          <label className={styles.field}>
            <span className={styles.label}>Contraseña</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={styles.input}
            />
          </label>

          {error && (
            <div role="alert" className={styles.error}>
              {error}
            </div>
          )}

          <Button type="submit" variant="primary" disabled={submitting}>
            {submitting ? 'Entrando…' : 'Entrar'}
          </Button>

          <p style={{ textAlign: 'center', margin: '16px 0 0', fontSize: 14 }}>
            <Link to="/forgot-password" style={{ color: '#7047eb' }}>¿Olvidaste tu contraseña?</Link>
          </p>
        </form>
      </div>
    </main>
  );
}
